import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import he from 'he';
import axiosPrivate from 'config/axiosPrivate';

import '../admin/textDisplay.css';
import './textDisplay.css';

// Redux Store
import {
  adminClientRedactionDataSelector,
  clearAdminClientRedactionData,
  setCurrentRedaction,
} from 'store/AdminClient/RedactionData';
import {
  fetchAdminClientTextOrderDisplay,
  fetchadminCLientOrderDisplay,
} from 'store/AdminClient/OrderDisplay/features';
import { adminCLientOrderDisplaySelector } from 'store/AdminClient/OrderDisplay';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import { UserSessionSelector } from 'store/UserSession';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

// Context Providers
import { WordsProvider } from 'utils/contexts/ScoreBoldWordsContext';
import { SemanticScoreProvider } from 'utils/contexts/SemanticScoreContext';

// Components
import { Form } from '@unform/web';
import AnalyseText from 'components/Admin/AnalyseText';
import { CustomEditor } from 'components/shared/CustomEditor';
import { Loader } from 'components/shared/Loader';
import ComparisonModal from 'components/shared/modal/ComparisonModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import KeywordStuffingModal from 'components/shared/modal/KeywordStuffingModals/KeywordStuffingModal';
import WordPressDeployModal from 'components/shared/modal/WordPressDeployModal';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';
import SmartProjectTextDetails from 'components/AdminClient/SmartProjectTextDetails';
import SmartTextTranslationView from 'components/shared/SmartcontentModal/SmartTextTranslationView';
import 'components/shared/SmartcontentModal/SmartContentPopintrad.css';
import { getTextFromHtml } from 'utils/smartContent/smartContentGenerationUtils';
import { NAVIGATING_TO_TRANSLATION_IA_KEY } from 'utils/smartContent/useSmartTextTranslation';
import { WordCountDisplay } from 'components/shared/TextDisplay/WordCountDisplay';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { RevisionDetails } from 'components/shared/RevisionDetails';

// Helpers
import { checkDataAdmin } from 'helpers/formHelper';
import {
  extractStrKeywordsWithFrequency,
  transformToSemanticGap,
  extractKeywordsWithFrequency,
  formatKeywordData,
} from 'helpers/FormatKeywordsHelper';
import { autoSaveText } from 'helpers/CreateAutoSave';


import {
  ROLES,
  GOVERNED_WRITERS_WITH_VALIDATION,
  KeywordStuffingModalPrompts,
} from 'utils/Constants';
import { getScoringLanguageLabel } from 'helpers/ProjectLanguageHelper';
import { extractWords } from 'utils/keywordStuffing/keywordStuffing';
import { keywordsAutoGeneration } from 'utils/KeywordsAutoGeneration/keywordsAutoGeneration';

import { useAutosave } from 'utils/hooks/useAutoSave';
import { useSidebar } from 'utils/hooks/features/textDisplay/useSidebar';
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useWordCountTracker } from 'utils/hooks/features/textDisplay/useWordCountTracker';
import { useTextStatus } from 'utils/hooks/features/textDisplay/useTextStatus';

import { useAdminClientTextForm } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextForm';
import { useAdminClientTextSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextSave';
import { useAdminClientDeliverText } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientDeliverText';
import { useAdminClientKeywordStuffing } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientKeywordStuffing';
import { useAdminClientFormValidation } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientFormValidation';
import { useAdminClientPutToDraft } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientPutToDraft';
import { useAdminClientAutoSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientAutoSave';
import usePageTracking from 'utils/hooks/usePageTracking';
import { isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';

import { replaceTextInBold, entitiesToSpecialChars, removeStrongTags } from 'utils/StringUtils';
import { navigateBackOrDashboard } from 'utils/navigation/safeBackNavigation';

const ADMIN_CLIENT_TEXT_DETAILS_PAGE = '/AdminClient/TextDetails';
const ADMIN_CLIENT_DASHBOARD = '/AdminClient/AdminMainMenu';
const wordsInBold = [];
const semanticScore = 0;

function AdminClientTextDisplayContent() {
  const RED = '#EE3900';
  const ORANGE = '#FF8D4B';
  const BLACK = '#3c3c3c';

  const [analyseCount, setAnalyseCount] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSendRevisionDisabled, setIsSendRevisionDisabled] = useState(false);
  const [isDraftDisabled, setIsDraftDisabled] = useState(true);

  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');

  const [showComparison, setShowComparison] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showSaveBeforeDownloadModal, setShowSaveBeforeDownloadModal] = useState(false);
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [isQuitSaving, setIsQuitSaving] = useState(false);

  const [color, setColor] = useState(BLACK);
  const [allWebsitesArePresent, setAllWebsitesArePresent] = useState(false);
  const [autoKeywordsGenerationLoading, setAutokeywordGenerationLoading] =
    useState(false);
  const [validTextNbr, setValidTextNbr] = useState(false);
  const [isAutomaticTextAnalysis, setIsAutomaticTextAnalysis] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isGoingOnline, setIsGoingOnline] = useState(false);
  const [showWordPressDeployModal, setShowWordPressDeployModal] = useState(false);
  const [wordPressDeployContent, setWordPressDeployContent] = useState('');
  const [hasPerformedInitialSave, setHasPerformedInitialSave] = useState(false);
  const [shouldReloadAfterSave, setShouldReloadAfterSave] = useState(false);
  const [savedTextId, setSavedTextId] = useState(null);
  const [translationUi, setTranslationUi] = useState(null);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isConsultantRoute = location.pathname.startsWith('/Consultant');
  const textDetailsPage = isConsultantRoute
    ? '/Consultant/TextDetails'
    : ADMIN_CLIENT_TEXT_DETAILS_PAGE;
  const dashboardPage = isConsultantRoute
    ? '/Consultant/MainMenu'
    : ADMIN_CLIENT_DASHBOARD;
  const projectDetailsPage = isConsultantRoute
    ? '/Consultant/DetailsProject'
    : '/AdminClient/DetailsProject';
  const params = useParams();
  const idFromPathname = (() => {
    const matched = location.pathname.match(/\/TextDetails\/([^/?#]+)/i);
    return matched?.[1] ? decodeURIComponent(matched[1]) : null;
  })();
  const routeTextId = params?.id || idFromPathname || null;
  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const { currentRedaction, isLoading, isError } = useSelector(
    adminClientRedactionDataSelector
  );
  const { order } = useSelector(adminCLientOrderDisplaySelector);
  const { clients } = useSelector(adminClientClientListSelector) || {};
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');

  const redactionData = currentRedaction;

  const projectLanguage = useMemo(
    () =>
      getScoringLanguageLabel(
        order?.website?.language || order?.language
      ),
    [order?.website?.language, order?.language]
  );

  const websiteCountry = useMemo(() => {
    if (order?.website?.country) return order.website.country;
    if (redactionData?.country) return redactionData.country;

    const clientsList = Array.isArray(clients) ? clients : [];
    const websiteId =
      redactionData?.websiteId || order?.websiteId || order?.website?.id;
    if (websiteId != null && clientsList.length > 0) {
      const byId = clientsList.find((w) => String(w?.id) === String(websiteId));
      if (byId?.country) return byId.country;
    }

    const normalizeUrl = (input) =>
      (input || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/+$/, '');

    const candidates = [
      selectedWebsiteUrl,
      order?.website?.urlSite,
      order?.website?.url,
      redactionData?.orderWebsite,
    ].filter(Boolean);

    for (const candidate of candidates) {
      const normalized = normalizeUrl(candidate);
      const match = clientsList.find((w) => {
        const wUrl = normalizeUrl(w?.urlSite || w?.url || '');
        const wName = normalizeUrl(w?.name || '');
        return wUrl === normalized || wName === normalized;
      });
      if (match?.country) return match.country;
    }

    return null;
  }, [
    order?.website?.country,
    order?.website?.id,
    order?.website?.urlSite,
    order?.website?.url,
    order?.websiteId,
    redactionData?.country,
    redactionData?.websiteId,
    redactionData?.orderWebsite,
    selectedWebsiteUrl,
    clients,
  ]);

  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  useEffect(() => {
    const projectId = redactionData?.projectId || redactionData?.orderId;
    if (!projectId) return;
    if (String(order?.id) === String(projectId)) return;
    dispatch(fetchadminCLientOrderDisplay(projectId));
  }, [redactionData?.projectId, redactionData?.orderId, order?.id, dispatch]);

  const normalizedOrderTextType = (order?.textType || '').toUpperCase();
  const normalizedOrderType = (order?.type || '').toUpperCase();
  const isSmartProject = normalizedOrderType.startsWith('SMART');
  const normalizedRedactionTextType = (
    redactionData?.type ||
    redactionData?.textType ||
    ''
  ).toUpperCase();
  const isTranslationText =
    normalizedOrderTextType === 'TRANSLATION' ||
    normalizedRedactionTextType === 'TRANSLATION' ||
    normalizedRedactionTextType === 'TRANSLATED';
  const uiTextType = isTranslationText ? 'TRANSLATION' : 'EDITORIAL';
  const backendTextType =
    redactionData?.type ||
    (isTranslationText ? 'TRANSLATED' : 'NON_TRANSLATED');

  const [currentContentScore, setCurrentContentScore] = useState(null);

  const formRef = useRef(null);
  const isInitialized = useRef(false);

  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarRef,
    handleOpenSidebarAndScrollTop,
    showSidebar,
    handleScrollBottom,
  } = useSidebar(uiTextType);

  const {
    textTitle,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    actualTextContent,
    actualHtmlContent,
    analyseText,
    analyseResult,
    keywords,
    combinedKeywords,
    list,
    stuffedKeywords,
    websites,
    initialTextContent,
    initialHtmlContent,
    // Setters
    setTextTitle,
    setKeyword,
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    setActualTextContent,
    setActualHtmlContent,
    setAnalyseText,
    setAnalyseResult,
    setKeywords,
    setCombinedKeywords,
    setCombinedKeywordsList,
    setList,
    setStuffedKeywords,
    setWebsites,
    setInitialTextContent,
    setInitialHtmlContent,
  } = useAdminClientTextForm(redactionData, 'SMART', isLoading);


  const isTextSaved = useMemo(() => {
    if (hasPerformedInitialSave || routeTextId) return true;

    const hasPersistedContent =
      !!redactionData?.id &&
      Boolean(
        redactionData?.actualHtmlContent?.trim()?.length ||
        redactionData?.actualTextContent?.trim()?.length ||
        redactionData?.initialHtmlContent?.trim()?.length ||
        redactionData?.initialTextContent?.trim()?.length
      );

    return hasPersistedContent;
  }, [
    hasPerformedInitialSave,
    routeTextId,
    redactionData?.id,
    redactionData?.actualHtmlContent,
    redactionData?.actualTextContent,
    redactionData?.initialHtmlContent,
    redactionData?.initialTextContent,
  ]);

  const handleDownloadWord = useCallback(() => {
    if (!isTextSaved || !redactionData?.id) {
      setShowSaveBeforeDownloadModal(true);
      return;
    }
    DownloadOneTextWordType(redactionData.id, user);
  }, [isTextSaved, redactionData?.id, user]);

  const navigateBack = useCallback(() => {
    navigateBackOrDashboard(history, dashboardPage, location.state);
  }, [history, dashboardPage, location.state]);

  const handleBackClick = useCallback(() => {
    if (!isTextSaved) {
      setShowQuitModal(true);
      return;
    }
    navigateBack();
  }, [isTextSaved, navigateBack]);

  const handleQuitWithoutSaving = useCallback(() => {
    setShowQuitModal(false);
    navigateBack();
  }, [navigateBack]);

  const {
    editorRef,
    stepOneEditorRef,
    editorInitialized,
    stepOneEditorInitialized,
    handleEditorInit,
    handleStepOneEditorInit,
  } = useEditorManagement();

  const {
    wordCount: initialTextWordCount,
    setWordCount: setInitialTextWordCount,
    getWordCount,
  } = useWordCountTracker(stepOneEditorRef, stepOneEditorInitialized);

  const {
    wordCount: actualTextWordCount,
    debouncedSetWordCount: debouncedSetActualTextWordCount,
  } = useWordCountTracker(editorRef, editorInitialized);

  const computedInitialWordCount = useMemo(() => {
    if (stepOneEditorRef.current && stepOneEditorInitialized) {
      try {
        const count = stepOneEditorRef.current.plugins?.wordcount?.getCount();
        if (count !== undefined && count !== null) {
          return count;
        }
      } catch (e) {
      }
    }

    if (initialTextContent) {
      const text = new DOMParser()
        .parseFromString(initialTextContent, 'text/html')
        .documentElement.textContent || '';
      const words = text.trim().split(/\s+/).filter(word => word.length > 0);
      return words.length;
    }

    return initialTextWordCount || 0;
  }, [initialTextContent, stepOneEditorInitialized, initialTextWordCount]);


  const {
    isPremium: hasInitialSection,
    hasInitialTextContent,
    isNotTranslation: isEditorial,
    isDelivered,
    isRevisionDone,
    isRevisionSent,
    isDraft,
    isEmpty,
  } = useTextStatus(redactionData, 'SMART', user.role, uiTextType);

  const { deliverText, isTextDelivered, setIsTextDelivered } =
    useAdminClientDeliverText({
      t,
      textType: uiTextType,
      combinedKeywords,
      setErrorSuccessMsg,
      setIsSuccess,
      setErrorSuccess,
      titleTag,
      metaDescriptionTag,
      setIsFetchLoading,
      actualHtmlContent,
      actualTextContent,
      semanticScore,
      initialHtmlContent,
      initialTextContent,
      keyword,
      textTitle,
      actualTextWordCount,
      initialTextWordCount,
      url,
      websites,
      setErrorSuccessImg,
      redactionId: redactionData?.id,
      formData: redactionData,
      currentContentScore: currentContentScore,
    });

  const {
    keywordStuffingLoading,
    initialStuffedKeywords,
    showKeywordStuffingModal,
    keywordStuffingModalTitle,
    keywordStuffingModalMsg,
    keywordStuffingModalData,
    keywordStuffingModalCloseBtn,
    keywordStuffingCall,
    setShowKeywordStuffingModal,
    setInitialStuffedKeywords,
  } = useAdminClientKeywordStuffing({
    setSidebarCollapsed,
    handleScrollBottom,
    actualHtmlContent,
    setActualHtmlContent,
    textTitle,
    keywords,
    extractWords,
    textType: uiTextType,
    initialTextContent,
    actualTextContent,
    initialTextWordCount,
    actualTextWordCount,
    setStuffedKeywords,
    deliverText,
    t,
    setActualTextContent,
    setInitialHtmlContent,
  });

  const { handleSave, isTextFieldDisabled } = useAdminClientTextSave({
    redactionData: redactionData,
    initialHtmlContent,
    initialTextContent,
    actualTextContent,
    actualHtmlContent,
    actualTextWordCount,
    initialTextWordCount,
    combinedKeywords,
    keyword,
    metaDescriptionTag,
    textTitle,
    titleTag,
    url,
    textType: backendTextType,
    stuffedKeywords,
    websites,
    initialStuffedKeywords,
    analyseResult,
    redactionId: redactionData?.id,
    setConfirmation,
    setActualTextContent,
    setActualHtmlContent,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
    t,
    websiteId: redactionData?.websiteId || order?.websiteId || 0,
    aiAgentId: redactionData?.aiAgentId || '',
    aiAgentModel: redactionData?.aiAgentModel || '',
    projectId: redactionData?.projectId || redactionData?.orderId || order?.id || null,
    currentContentScore: currentContentScore,
    currentSemanticGap: list,
  });

  const handleSaveDraftAndQuit = useCallback(async () => {
    if (isQuitSaving) return;
    try {
      setIsQuitSaving(true);
      await handleSave('manualDraft');
      setHasPerformedInitialSave(true);
      setShowQuitModal(false);
      navigateBack();
    } catch (error) {
      setShowQuitModal(false);
    } finally {
      setIsQuitSaving(false);
    }
  }, [isQuitSaving, handleSave, navigateBack]);

  const { putToDraft } = useAdminClientPutToDraft({
    t,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    metaDescriptionTag,
    list,
    combinedKeywords,
    websites,
    stuffedKeywords,
    textTitle,
    titleTag,
    url,
    textId: redactionData?.id,
    projectId: order?.id || redactionData?.orderId,
    initialTextContent,
    initialHtmlContent,
    initialStuffedKeywords,
    possibleStuffedKeywords: [],
    actualTextWordCount,
    initialTextWordCount,
    type: backendTextType,
    activeInitialText: redactionData?.activeInitialText,
    totalNumberOfTextAdded: 0,
    contentScore: currentContentScore || redactionData?.contentScore || null,
    writerProjectId: redactionData?.writerProjectId || 0,
    websiteId: redactionData?.websiteId || order?.websiteId || 0,
    aiAgentId: redactionData?.aiAgentId || '',
    currentSemanticGap: list,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
  });

  const { autoSave } = useAdminClientAutoSave({
    textId: redactionData?.id,
  });

  const lastContentScoreRef = useRef(null);
  const lastGoodContentScoreRef = useRef(
    Number(redactionData?.contentScore?.globalScore) > 0
      ? redactionData.contentScore
      : Number(redactionData?.initialScoreData?.globalScore) > 0
        ? redactionData.initialScoreData
        : null
  );
  const resolveContentScoreForSaveRef = useRef(null);
  const autosaveReadyRef = useRef(false);
  const loadedTextIdRef = useRef(redactionData?.id);

  if (loadedTextIdRef.current !== redactionData?.id) {
    loadedTextIdRef.current = redactionData?.id;
    autosaveReadyRef.current = false;
    lastContentScoreRef.current = null;
    lastGoodContentScoreRef.current =
      Number(redactionData?.contentScore?.globalScore) > 0
        ? redactionData.contentScore
        : Number(redactionData?.initialScoreData?.globalScore) > 0
          ? redactionData.initialScoreData
          : null;
  }

  useEffect(() => {
    if (!editorInitialized) return undefined;
    const timer = setTimeout(() => {
      autosaveReadyRef.current = true;
    }, 0);
    return () => clearTimeout(timer);
  }, [redactionData?.id, editorInitialized]);

 
  const isHealthyContentScore = useCallback((score) => {
    if (!score || typeof score !== 'object') return false;
    return Number(score.globalScore) > 0;
  }, []);

  const rememberGoodContentScore = useCallback((score) => {
    if (isHealthyContentScore(score)) {
      lastGoodContentScoreRef.current = score;
    }
  }, [isHealthyContentScore]);

  const resolveContentScoreForSave = useCallback((candidate) => {
    if (isHealthyContentScore(candidate)) return candidate;
    if (isHealthyContentScore(currentContentScore)) return currentContentScore;
    if (isHealthyContentScore(lastGoodContentScoreRef.current)) {
      return lastGoodContentScoreRef.current;
    }
    if (isHealthyContentScore(redactionData?.contentScore)) {
      return redactionData.contentScore;
    }
    if (isHealthyContentScore(redactionData?.initialScoreData)) {
      return redactionData.initialScoreData;
    }
    return lastGoodContentScoreRef.current;
  }, [
    currentContentScore,
    isHealthyContentScore,
    redactionData?.contentScore,
    redactionData?.initialScoreData,
  ]);

  useEffect(() => {
    rememberGoodContentScore(currentContentScore);
  }, [currentContentScore, rememberGoodContentScore]);

  useEffect(() => {
    resolveContentScoreForSaveRef.current = resolveContentScoreForSave;
  }, [resolveContentScoreForSave]);

  // Résout le contentScore au moment du flush (pas à la planification).
  const autoSaveWithHealthyScore = useCallback(
    async (...args) => {
      const nextArgs = [...args];
      const resolve = resolveContentScoreForSaveRef.current;
      nextArgs[9] =
        typeof resolve === 'function' ? resolve(nextArgs[9]) : nextArgs[9];
      return autoSave(...nextArgs);
    },
    [autoSave]
  );

  const { scheduleSave, isSaving } = useAutosave(autoSaveWithHealthyScore, 30000);

  const buildAutoSaveRedactionData = useCallback(() => ({
    id: redactionData?.id,
    numberWords:
      Number(redactionData?.numberWords) ||
      Number(order?.maxWords) ||
      Number(order?.numberOfWords) ||
      Number(order?.numberWords) ||
      0,
    semanticGap: [],
    axeRedaction: redactionData?.axeRedaction || redactionData?.writingAxis || '',
    retranscription: String(redactionData?.retranscription || '').trim(),
    internalLinking: websites || redactionData?.internalLinking || '',
  }), [redactionData, order, websites]);

  const handleScoreDataChange = useCallback(
    async (scoreData) => {
      const previousScore = lastContentScoreRef.current;
      const hasGoodScoreAlready =
        isHealthyContentScore(previousScore) ||
        isHealthyContentScore(currentContentScore) ||
        isHealthyContentScore(lastGoodContentScoreRef.current);

      // Ignore un score vidé s'il écraserait un bon score (cause du wipe du gap).
      if (!isHealthyContentScore(scoreData) && hasGoodScoreAlready) {
        return;
      }

      setCurrentContentScore(scoreData);
      lastContentScoreRef.current = scoreData;
      rememberGoodContentScore(scoreData);

      const prevGlobal = previousScore?.globalScore ?? null;
      const nextGlobal = scoreData?.globalScore ?? null;

      if (!redactionData?.id || nextGlobal == null) {
        return;
      }

      // Le premier score est celui déjà chargé avec le texte.
      // L'auto-save ne part que si le score change ensuite.
      if (prevGlobal === null || prevGlobal === nextGlobal) {
        return;
      }

      if (!isHealthyContentScore(scoreData)) {
        return;
      }

      if (isConsultantRoute || user?.role === ROLES.CONSULTANT) {
        return;
      }

      try {
        await autoSave(
          actualTextContent || '',
          actualHtmlContent || '',
          buildAutoSaveRedactionData(),
          initialHtmlContent,
          initialTextContent,
          'SMART',
          url || redactionData?.url || '',
          titleTag || redactionData?.titleTag || '',
          metaDescriptionTag || redactionData?.metaDescriptionTag || '',
          scoreData
        );
      } catch (e) {
        console.error('Auto-save après mise à jour du contentScore a échoué:', e);
      }
    },
    [
      autoSave,
      actualTextContent,
      actualHtmlContent,
      buildAutoSaveRedactionData,
      currentContentScore,
      isHealthyContentScore,
      rememberGoodContentScore,
      initialHtmlContent,
      initialTextContent,
      url,
      titleTag,
      metaDescriptionTag,
      redactionData?.id,
      redactionData?.url,
      redactionData?.titleTag,
      redactionData?.metaDescriptionTag,
      isConsultantRoute,
      user?.role,
    ]
  );

  const { checkValidForm, disabledForm } = useAdminClientFormValidation({
    actualTextContent,
    actualTextWordCount,
    analyseResult,
    formData: redactionData,
    initialTextWordCount,
    keyword,
    metaDescriptionTag,
    minWords: 0,
    textTitle,
    textType: uiTextType,
    titleTag,
    validTextNbr,
    semanticScore,
  });


  const enableSave = useCallback(() => {
    if (!redactionData || isEmpty(url)) {
      setIsSaveDisabled(true);
      return;
    }
    setIsSaveDisabled(false);
  }, [redactionData, url]);

  const reloadRedactionData = useCallback(
    async (textId) => {
      if (!textId) return;

      try {
        setIsFetchLoading(true);
        let res;
        try {
          res = await axiosPrivate.get(`/texts/external/${textId}/fullDetails`);
        } catch (assignedError) {
          if (!isConsultantRoute) throw assignedError;
          res = await axiosPrivate.get(`/texts/external/unassigned/${textId}/fullDetails`);
        }
        const d = res?.data || {};

        // Préserver le currentContentScore existant si l'API ne le retourne pas
        const preservedContentScore =
          d.contentScore ||
          currentContentScore ||
          redactionData?.contentScore ||
          null;

        dispatch(
          setCurrentRedaction({
            id: d.id || textId,
            title: d.textTitle || d.keyword || '',
            textTitle: d.textTitle || d.keyword || '',
            keyword: d.keyword || '',
            url: d.url || '',
            titleTag: d.titleTag || '',
            metaDescriptionTag: d.metaDescriptionTag || '',
            initialHtmlContent: d.initialHtmlContent || '',
            initialTextContent: d.initialTextContent || '',
            actualHtmlContent: d.actualHtmlContent || '',
            actualTextContent: d.actualTextContent || '',
            semanticGap: d.semanticGap || [],
            analyseResult: d.analyseResult || null,
            stuffedKeywords: d.stuffedKeywords || [],
            internalLinking: d.internalLinking || '',
            numberWords: Number(d.numberWords) || 0,
            axeRedaction: d.axeRedaction || d.writingAxis || '',
            retranscription: d.retranscription || '',
            delivered: !!d.delivered,
            inRevision: !!d.inRevision,
            revisionSent: !!d.revisionSent,
            revisionDone: !!d.revisionDone,
            globalRevisionSent: !!d.globalRevisionSent,
            validated: !!d.validated,
            deployed: !!d.deployed,
            orderId: d.projectId || redactionData?.orderId || order?.id,
            createdAt: d.createdAt || redactionData?.createdAt || new Date().toISOString(),
            initialScoreData: d.initialScoreData || null,
            type: d.type || redactionData?.type || '',
            textType: d.type || redactionData?.textType || '',
            frontendType: d.type || redactionData?.frontendType || '',
            websiteId: d.websiteId || redactionData?.websiteId || order?.websiteId || 0,
            aiAgentId: d.aiAgentId || redactionData?.aiAgentId || '',
            aiAgentModel: d.aiAgentModel || redactionData?.aiAgentModel || '',
            projectId: d.projectId || redactionData?.projectId || redactionData?.orderId || order?.id,
            contentScore: preservedContentScore,
            writerProjectId: d.writerProjectId || redactionData?.writerProjectId || 0,
            knowledgeBaseFiles: Array.isArray(d.knowledgeBaseFiles)
              ? d.knowledgeBaseFiles
              : [],
            activeInitialText:
              d.activeInitialText !== undefined
                ? d.activeInitialText
                : redactionData?.activeInitialText !== undefined
                  ? redactionData.activeInitialText
                  : true,
          })
        );

        // Mettre à jour currentContentScore si on a un nouveau score depuis l'API
        if (d.contentScore && Number(d.contentScore.globalScore) > 0) {
          setCurrentContentScore(d.contentScore);
          lastGoodContentScoreRef.current = d.contentScore;
        } else if (preservedContentScore && Number(preservedContentScore.globalScore) > 0) {
          setCurrentContentScore(preservedContentScore);
          lastGoodContentScoreRef.current = preservedContentScore;
        } else if (d.contentScore) {
       
          if (!lastGoodContentScoreRef.current && !currentContentScore) {
            setCurrentContentScore(d.contentScore);
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors du rechargement des données:', error);
      } finally {
        setIsFetchLoading(false);
      }
    },
    [dispatch, order, redactionData, currentContentScore, isConsultantRoute]
  );


  useEffect(() => {
    const textIdFromStorage = (() => {
      try {
        return localStorage.getItem('AdminClient_TextId');
      } catch {
        return null;
      }
    })();

    if (routeTextId) {
      try {
        localStorage.setItem('AdminClient_TextId', String(routeTextId));
      } catch { }
      if (
        String(redactionData?.id || '') !== String(routeTextId) ||
        !Array.isArray(redactionData?.knowledgeBaseFiles)
      ) {
        reloadRedactionData(routeTextId);
      }
      return;
    }

    if (redactionData?.id) {
      try {
        localStorage.setItem('AdminClient_TextId', String(redactionData.id));
      } catch { }
      if (location.pathname === textDetailsPage) {
        history.replace(
          `${textDetailsPage}/${redactionData.id}${location.search || ''}`
        );
      }
      return;
    }

    if (textIdFromStorage) {
      reloadRedactionData(textIdFromStorage);
    }
  }, [
    routeTextId,
    redactionData?.id,
    reloadRedactionData,
    history,
    location.pathname,
    location.search,
    textDetailsPage,
  ]);

  function handleKeywordChange(e) {
    const newValue = e.target.value;
    setCombinedKeywords(newValue);
    setKeywords(extractStrKeywordsWithFrequency(newValue));

    const newGap = transformToSemanticGap(newValue);
    setList(newGap);
  }

  function handleGapUpdate(newGap) {
    setList(newGap);
    setKeywords(extractKeywordsWithFrequency(newGap));
    setCombinedKeywords(formatKeywordData(newGap));
  }

  const enableSendRevision = useCallback(() => {
    if (redactionData && redactionData.revisionDone) {
      setIsSendRevisionDisabled(true);
      return;
    }
    setIsSendRevisionDisabled(false);
  }, [redactionData]);

  const enableDraft = useCallback(() => {

    const hasContent = actualTextContent && actualTextContent.trim().length > 0;
    setIsDraftDisabled(!hasContent);
  }, [actualTextContent]);

  async function validateData(data, { reset }, event) {
    let action = event.nativeEvent.submitter.value;
    console.log('🔍 Action du bouton:', action);

    let finalHtmlContent = actualHtmlContent;

    if (action === 'PutToDraft' || action === 'DeliverText') {
      console.log('🔍 Application du highlighting...');
      finalHtmlContent = await applyHighlightingAndWait();
    }

    const wrappedPutToDraft = (demande) => {
      console.log('🔍 wrappedPutToDraft appelé avec:', demande);
      return putToDraft(demande, finalHtmlContent);
    };
    const wrappedDeliverText = (demande) =>
      deliverText(demande, finalHtmlContent);

    console.log('🔍 Appel à checkDataAdmin avec action:', action);
    await checkDataAdmin(
      data,
      'SMART', // orderType
      formRef,
      action,
      wrappedPutToDraft,
      wrappedDeliverText,
      undefined, // EditText
      undefined, // InvalidateText
      undefined, // ReactivateInitialText
      undefined, // VerifyAndDeliverText
      undefined // VerifyText
    );
  }

  function resetFields() {
    setTextTitle('');
    setKeyword('');
    setUrl('');
    setTitleTag('');
    setMetaDescriptionTag('');
    setActualTextContent('');
    setActualHtmlContent('');
    setInitialTextContent('');
    setInitialHtmlContent('');
    setAnalyseResult('');
    setKeywords('');
    setCombinedKeywords('');
    setWebsites('');
    setInitialStuffedKeywords([]);
    setStuffedKeywords([]);
    setList({});
    setIsTextDelivered(false);
  }

  const handleInputChange = (e) => {
    setWebsites(e.target.value);
    checkIfAllLinesGreen(actualHtmlContent, e.target.value);
  };

  const checkIfAllLinesGreen = (isGreen) => {
    setAllWebsitesArePresent(isGreen);
  };

  const highlightWordsInHtml = (htmlContent, wordsToHighlight) => {
    if (!wordsToHighlight || wordsToHighlight.length === 0) {
      return htmlContent;
    }

    let processedContent = he.decode(htmlContent);
    let filteredKeywords = wordsToHighlight.filter((keyword) => {
      return keyword.length > 0 && keyword !== '';
    });

    return replaceTextInBold(processedContent, filteredKeywords);
  };

  const handleSendToRevision = async () => {
    setActionsOpen(false);
    const textId = redactionData?.id;
    if (!textId) return;

    try {
      await axiosPrivate.put(
        `/texts/external/${textId}/update-status?textStatusType=${encodeURIComponent('IN_REVISION')}`
      );
      await reloadRedactionData(textId);
      setErrorSuccessMsg(t('Le texte a été mis en révision avec succès.'));
      setIsSuccess(true);
      setErrorSuccess(true);
    } catch (error) {
      if (isTextRevisionNotAllowedError(error)) {
        setIsRevisionErrorModalOpen(true);
      } else {
        console.error('Erreur lors de la mise en révision:', error);
        setErrorSuccessMsg(t('Erreur lors de la mise en révision'));
        setIsSuccess(false);
        setErrorSuccess(true);
      }
    }
  };

  const handleGoOnline = async () => {
    const textId = redactionData?.id;
    if (!textId || isGoingOnline) return;

    setActionsOpen(false);
    setIsGoingOnline(true);
    try {
      const payload = {
        textId: textId || '',
        deployed: true,
        url: url || redactionData?.url || '',
      };
      await axiosPrivate.post('/texts/external/deploy-new-url', payload);
      await reloadRedactionData(textId);
      setErrorSuccessMsg(t('Texte mis en ligne avec succès'));
      setIsSuccess(true);
      setErrorSuccess(true);
    } catch (error) {
      console.error('Erreur lors de la mise en ligne:', error);
      setErrorSuccessMsg(
        error?.response?.data?.message ||
        error.message ||
        t('Erreur lors de la mise en ligne')
      );
      setIsSuccess(false);
      setErrorSuccess(true);
    } finally {
      setIsGoingOnline(false);
    }
  };

  const handleWordPressDeploy = async () => {
    const textId = redactionData?.id;
    if (!textId || isDeploying) return;

    setActionsOpen(false);
    const finalHtml = await applyHighlightingAndWait();
    setWordPressDeployContent(finalHtml || actualHtmlContent || '');
    setShowWordPressDeployModal(true);
  };

  const handleWordPressDeploySuccess = async () => {
    const textId = redactionData?.id;
    if (!textId) return;

    setIsDeploying(true);
    try {
      await reloadRedactionData(textId);
      setErrorSuccessMsg(t('Texte déployé avec succès sur WordPress'));
      setIsSuccess(true);
      setErrorSuccess(true);
    } catch (error) {
      console.error('Erreur lors du rechargement après déploiement:', error);
      setErrorSuccessMsg(
        error?.response?.data?.message ||
        error.message ||
        t('Texte déployé, mais erreur lors du rechargement des données.')
      );
      setIsSuccess(false);
      setErrorSuccess(true);
    } finally {
      setIsDeploying(false);
    }
  };

  const applyHighlightingAndWait = () => {
    return new Promise((resolve) => {
      if (!editorInitialized || !editorRef.current) {
        resolve(actualHtmlContent);
        return;
      }

      const editor = editorRef.current;
      const currentRawHtml = editor.getContent();
      const newHighlightedHtml = highlightWordsInHtml(
        currentRawHtml,
        wordsInBold
      );

      if (newHighlightedHtml !== currentRawHtml) {
        editor.setContent(newHighlightedHtml);
        setActualHtmlContent(() => {
          resolve(newHighlightedHtml);
          return newHighlightedHtml;
        });
        setActualTextContent(editor.getContent({ format: 'text' }));
      } else {
        resolve(actualHtmlContent);
      }
    });
  };

  useEffect(() => {
    document.title = "Typix - Mes contenus";
  }, []);

  useEffect(() => {
    document.title = t('Typix - Détails du texte');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);

  useEffect(() => {
    setHasPerformedInitialSave(false);
    setShouldReloadAfterSave(false);
    setSavedTextId(null);
  }, [redactionData?.id]);



  useEffect(() => {
    enableSave();
  }, [redactionData, url, initialTextContent, enableSave]);

  useEffect(() => {
    checkValidForm();
  }, [checkValidForm]);

  useEffect(() => {
    enableSendRevision();
  }, [enableSendRevision, redactionData]);

  useEffect(() => {
    enableDraft();
  }, [enableDraft]);

  useEffect(() => {
    return () => {
      if (order?.id) {
        dispatch(fetchAdminClientTextOrderDisplay(order.id));
      }
    };
  }, [dispatch, order?.id]);

  // Nettoyer le localStorage lors du démontage du composant
  useEffect(() => {
    return () => {
      const isNavigatingToTranslationIA =
        sessionStorage.getItem(NAVIGATING_TO_TRANSLATION_IA_KEY) === 'true';
      // Ne pas effacer si on reste sur TextDetails/:id (remontage / navigation interne)
      const stayingOnTextDetails =
        typeof window !== 'undefined' &&
        (window.location.pathname.startsWith(ADMIN_CLIENT_TEXT_DETAILS_PAGE) ||
          window.location.pathname.startsWith('/Consultant/TextDetails'));
      if (!isNavigatingToTranslationIA && !stayingOnTextDetails) {
        localStorage.removeItem('AdminClient_TextId');
      }
    };
  }, []);

  if (isFetchLoading || isLoading || isError) return <Loader />;

  if (!redactionData) {
    return (
      <div className="content-center">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3> {t('❌ Aucune rédaction trouvée')}</h3>
          <p>
            {t('currentRedaction est null/undefined et aucune donnée dans localStorage')}
          </p>
          <p>{t('Redirection vers la page des projets...')}</p>
          <button
            onClick={() => history.push(projectDetailsPage)}
            className="btn btn-primary"
          >
            {t('Retour aux projets')}
          </button>
        </div>
      </div>
    );
  }

  const isConsultantUser =
    isConsultantRoute || user?.role === ROLES.CONSULTANT;

  const saveAsDraftFromActions = async () => {
    setActionsOpen(false);
    await applyHighlightingAndWait();
    const savedData = await handleSave('manualDraft');
    setHasPerformedInitialSave(true);

    const textIdToReload = savedData?.id || redactionData?.id;
    if (textIdToReload) {
      setSavedTextId(textIdToReload);
      setShouldReloadAfterSave(true);
    }
  };

  const renderTextActionsControls = () => {
    if (isConsultantUser) return null;

    return (
    <div className="d-flex">
      {!redactionData?.id || !isTextSaved ? (
        (() => {
          const hasUrl =
            (url && url.trim() !== '') ||
            (redactionData?.url && redactionData.url.trim() !== '');
          const orderTypeUpper = (order?.type || '').toUpperCase();
          const isSmartProjectOrder = orderTypeUpper.startsWith('SMART');
          const isPremiumProject =
            order?.type === 'PREMIUM' || (!order && hasUrl);
          const showInitialSection = isSmartProjectOrder
            ? hasUrl
            : (hasInitialSection || (isPremiumProject && hasUrl));

          if (showInitialSection) {
            return null;
          }

          return (
            <button
              type="button"
              className="btn"
              onClick={async () => {
                await applyHighlightingAndWait();
                const savedData = await handleSave('manualDraft');
                setHasPerformedInitialSave(true);

                const textIdToReload = savedData?.id || redactionData?.id;
                if (textIdToReload) {
                  setSavedTextId(textIdToReload);
                  setShouldReloadAfterSave(true);
                }
              }}
              disabled={!actualTextContent || actualTextContent.trim().length === 0}
              style={{
                background: (!actualTextContent || actualTextContent.trim().length === 0) ? '#f5f5f5' : '#6458F1',
                border: 'none',
                color: (!actualTextContent || actualTextContent.trim().length === 0) ? '#999' : 'white',
                borderRadius: '6px',
                padding: '12px 18px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                textTransform: 'none',
                cursor: (!actualTextContent || actualTextContent.trim().length === 0) ? 'not-allowed' : 'pointer',
                opacity: (!actualTextContent || actualTextContent.trim().length === 0) ? 0.6 : 1,
              }}
            >
              {t('Brouillon')}
            </button>
          );
        })()
      ) : (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="btn"
            onClick={() => setActionsOpen(!actionsOpen)}
            style={{
              background: '#6458F1',
              border: 'none',
              color: '#ffffff',
              borderRadius: '6px',
              padding: '10px 18px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
              textTransform: 'none',
              cursor: 'pointer'
            }}
          >
            {t('Actions')} | <i className={`fa fa-chevron-${actionsOpen ? 'down' : 'up'}`} />
          </button>
          {actionsOpen && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                bottom: '100%',
                marginBottom: 8,
                background: '#fff',
                borderRadius: 12,
                boxShadow:
                  '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
              }}
            >
              {isConsultantUser ? (
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: 12,
                    background: '#F5F3FF',
                    color: '#6458F1',
                    cursor: 'pointer',
                  }}
                  onClick={saveAsDraftFromActions}
                >
                  {t('Brouillon')}
                </div>
              ) : !redactionData?.delivered ? (
                <>
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      background: '#F5F3FF',
                      color: '#6458F1',
                      cursor: 'pointer'
                    }}
                    onClick={saveAsDraftFromActions}
                  >
                    {t('Brouillon')}
                  </div>
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      background: '#F5F3FF',
                      color: '#6458F1',
                    }}
                    onClick={async () => {
                      setActionsOpen(false);
                      const finalHtml = await applyHighlightingAndWait();
                      await deliverText('manualDeliver', finalHtml);
                      if (redactionData?.id) {
                        await reloadRedactionData(redactionData.id);
                      }
                    }}
                  >
                    {t('Livrer')}
                  </div>
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                      cursor: 'pointer',
                      background: '#F5F3FF',
                      color: '#6458F1',
                    }}
                    onClick={handleSendToRevision}
                  >
                    {t('En révision')}
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      borderTopLeftRadius: '6px',
                      borderTopRightRadius: '6px',
                      background: '#F5F3FF',
                      color: '#6458F1',
                      cursor: 'pointer'
                    }}
                    onClick={async () => {
                      setActionsOpen(false);
                      const finalHtml = await applyHighlightingAndWait();
                      try {
                        const payload = {
                          actualTextContent: actualTextContent || '',
                          actualHtmlContent: removeStrongTags(
                            finalHtml || actualHtmlContent || ''
                          ),
                        };
                        await axiosPrivate.put(`/texts/external/${redactionData?.id}/validate`, payload);
                        await reloadRedactionData(redactionData?.id);
                        setErrorSuccessMsg(t('Texte validé avec succès'));
                        setIsSuccess(true);
                        setErrorSuccess(true);
                      } catch (error) {
                        console.error('Erreur lors de la validation:', error);
                        setErrorSuccessMsg(t('Erreur lors de la validation'));
                        setIsSuccess(false);
                        setErrorSuccess(true);
                      }
                    }}
                  >
                    {t('Valider')}
                  </div>
                  {!redactionData?.deployed && (
                    <>
                      <div
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #f0f0f0',
                          cursor: isGoingOnline ? 'not-allowed' : 'pointer',
                          background: '#F5F3FF',
                          color: '#6458F1',
                          opacity: isGoingOnline ? 0.7 : 1,
                        }}
                        onClick={isGoingOnline ? undefined : handleGoOnline}
                      >
                        {isGoingOnline ? t('En ligne...') : t('En ligne')}
                      </div>
                      <div
                        style={{
                          padding: '12px 16px',
                          borderBottomLeftRadius: '6px',
                          borderBottomRightRadius: '6px',
                          cursor: isDeploying ? 'not-allowed' : 'pointer',
                          background: '#F5F3FF',
                          color: '#6458F1',
                          opacity: isDeploying ? 0.7 : 1,
                        }}
                        onClick={isDeploying ? undefined : handleWordPressDeploy}
                      >
                        {isDeploying ? t('Déploiement...') : t('Déployer via WordPress')}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
  };

  return (
    <div className="content-center">
      <Form
        ref={formRef}
        onSubmit={validateData}
        style={{
          transition: 'margin-right 0.3s ease',
          marginRight: sidebarCollapsed || !showSidebar ? 0 : sidebarWidth,
        }}
      >
        <FormHeader
          textTitle={
            redactionData?.title || textTitle || t('Nouveau texte AdminClient')
          }
          onBackClick={handleBackClick}
          onDownloadClick={handleDownloadWord}
          userRole={ROLES.ADMINCLIENT}
        />



        {isSmartProject ? (
          <SmartProjectTextDetails
            t={t}
            order={order}
            redactionData={redactionData}
            keyword={keyword}
            url={url}
            titleTag={titleTag}
            metaDescriptionTag={metaDescriptionTag}
            setKeyword={setKeyword}
            setUrl={setUrl}
            setTitleTag={setTitleTag}
            setMetaDescriptionTag={setMetaDescriptionTag}
            list={list}
            combinedKeywords={combinedKeywords}
            setList={setList}
            setKeywords={setKeywords}
            setCombinedKeywords={setCombinedKeywords}
            websites={websites}
            setWebsites={setWebsites}
            initialTextContent={initialTextContent}
            initialHtmlContent={initialHtmlContent}
            actualHtmlContent={actualHtmlContent}
            actualTextContent={actualTextContent}
            setActualHtmlContent={setActualHtmlContent}
            setActualTextContent={setActualTextContent}
            editorRef={editorRef}
            expandMetaField
            readOnly={isConsultantUser}
            onTranslationUiChange={setTranslationUi}
            onTranslationError={(message) => {
              setIsSuccess(false);
              setErrorSuccessMsg(message);
              setErrorSuccess(true);
            }}
          />
        ) : (
          <TextDetailsSection
            t={t}
            textId={redactionData?.id}
            expandMetaField
            disabledFields={
              isConsultantUser
                ? ['keyword', 'titleTag', 'metaDescriptionTag', 'url']
                : []
            }
            formData={{
              projectType: order?.type || '',
              id: redactionData?.id,
              textTitle,
              keyword,
              url,
              titleTag,
              metaDescriptionTag,
            }}
            handlers={{
              setTextTitle,
              setKeyword,
              setUrl,
              setTitleTag,
              setMetaDescriptionTag,
            }}
            onSettingsSaved={async () => {
              if (redactionData?.id) {
                await reloadRedactionData(redactionData.id);
              }
            }}
          />
        )}


        {(redactionData?.globalRevisionSent || redactionData?.revisionSent) &&
          redactionData?.revisionText && (
            <RevisionDetails
              t={t}
              textId={redactionData.id}
              revisionRequestAt={redactionData?.revisionRequestAt}
              revisionText={redactionData?.revisionText}
              revisionFileName={redactionData?.revisionFileName}
            />
          )}


        {(() => {
          const hasUrl = (url && url.trim() !== '') || (redactionData?.url && redactionData.url.trim() !== '');
          const orderTypeUpper = (order?.type || '').toUpperCase();
          const isSmartProject = orderTypeUpper.startsWith('SMART');
          const isPremiumProject = order?.type === 'PREMIUM' || (!order && hasUrl);

          const shouldShow = isSmartProject
            ? hasUrl
            : (hasInitialSection || (isPremiumProject && hasUrl));
          console.log('🔍 Debug InitialTextSection display:', {
            hasInitialSection,
            orderType: order?.type,
            isSmartProject,
            url,
            redactionDataUrl: redactionData?.url,
            urlTrimmed: url?.trim(),
            hasUrl,
            isPremiumProject,
            shouldShow,
          });
          return shouldShow;
        })() && (
            <div className="row mt-2">
              <InitialTextSection
                isTextFieldDisabled={isTextFieldDisabled}
                formData={redactionData}
                textType={uiTextType}
                url={url}
                initialHtmlContent={initialHtmlContent}
                initialTextContent={initialTextContent}
                handleStepOneEditorInit={handleStepOneEditorInit}
                setInitialHtmlContent={setInitialHtmlContent}
                setInitialTextContent={setInitialTextContent}
                setInitialTextWordCount={setInitialTextWordCount}
                userRole={isConsultantUser ? ROLES.CONSULTANT : user.role}
                t={t}
                isNotTranslation={isEditorial}
                isSaveDisabled={isSaveDisabled || isConsultantUser}
                isTextSaved={isTextSaved}
                textId={redactionData?.id}
                onDownloadWord={handleDownloadWord}
              />
            </div>
          )}

        {(() => {
          const hasUrl = (url && url.trim() !== '') || (redactionData?.url && redactionData.url.trim() !== '');
          const orderTypeUpper = (order?.type || '').toUpperCase();
          const isSmartProject = orderTypeUpper.startsWith('SMART');
          const isPremiumProject = order?.type === 'PREMIUM' || (!order && hasUrl);
          // Pour les projets smart : afficher seulement si le texte a une URL
          // Pour les autres projets : logique existante
          const shouldShow = isSmartProject
            ? hasUrl
            : (hasInitialSection || (isPremiumProject && hasUrl));

          if (!shouldShow || isTextSaved) return null;

          const handleInitialSave = async (event) => {
            event.preventDefault();
            try {
              const htmlFromEditor =
                stepOneEditorRef.current?.getContent?.() || '';
              const textFromEditor =
                stepOneEditorRef.current?.getContent?.({
                  format: 'text',
                }) || '';

              const htmlToCopy =
                (htmlFromEditor && htmlFromEditor.trim().length > 0)
                  ? htmlFromEditor
                  : (initialHtmlContent && initialHtmlContent.trim().length > 0)
                    ? initialHtmlContent
                    : actualHtmlContent || '';

              const textToCopy =
                (textFromEditor && textFromEditor.trim().length > 0)
                  ? textFromEditor
                  : (initialTextContent && initialTextContent.trim().length > 0)
                    ? initialTextContent
                    : actualTextContent || '';

              setActualHtmlContent(htmlToCopy);
              setActualTextContent(textToCopy);

              const savedData = await handleSave('manualDraft', {
                actualHtmlContent: htmlToCopy,
                actualTextContent: textToCopy,
              });

              setHasPerformedInitialSave(true);

              const textIdToReload = savedData?.id || redactionData?.id;
              if (textIdToReload) {
                setSavedTextId(textIdToReload);
                setShouldReloadAfterSave(true);
              }

              if (editorRef.current && editorInitialized) {
                const contentToSet = htmlToCopy || textToCopy || '';
                editorRef.current.setContent(contentToSet);
              }
            } catch (error) {
              console.error('❌ Erreur lors de la sauvegarde initiale :', error);
            }
          };

          return (
            <div className="row mt-2">
              <div className="col-12 d-flex justify-content-end">
                <button
                  style={{ backgroundColor: '#6458F1', color: 'white', borderRadius: '6px', padding: '10px 18px', cursor: 'pointer', border: 'none' }}
                  type="button"
                  onClick={handleInitialSave}
                >
                  {t('Enregistrer')}
                </button>
              </div>
            </div>
          );
        })()}

        <div className="row mt-2">
          <div className="col-12">
            {isSmartProject && translationUi?.isTranslationMode ? (
              <SmartTextTranslationView
                t={t}
                sourceContent={actualHtmlContent || actualTextContent || ''}
                translatedContent={translationUi.translatedContent}
                onTranslatedContentChange={translationUi.setTranslatedContent}
                sourceLanguageLabel={translationUi.sourceLanguageLabel}
                targetLanguageLabel={translationUi.translationTargetLanguage?.language || ''}
                isTranslating={translationUi.isTranslating}
                isSavingTranslation={translationUi.isSavingTranslation}
                onSaveTranslation={translationUi.onSaveTranslation}
                sourceTextId={redactionData?.id}
                getTextFromHtml={getTextFromHtml}
                renderSourceActions={renderTextActionsControls}
                readOnly={isConsultantUser}
              />
            ) : (
              <div>


                <div className="box-wrapper-editor">
                  <CustomEditor
                    disabled={false}
                    isReadOnly={isConsultantUser}
                    allowConsultantEdit={false}
                    textId={redactionData?.id}
                    onDownloadWord={handleDownloadWord}
                    downloadFileName={keyword || textTitle || redactionData?.title || 'document'}
                    content={
                      (actualHtmlContent && actualHtmlContent.length > 0)
                        ? actualHtmlContent
                        : actualTextContent
                    }
                    onInit={(evt, editor) => {
                      handleEditorInit(editor);
                      if (isConsultantUser) {
                        try {
                          if (editor?.mode?.set) editor.mode.set('readonly');
                          const body = editor?.getBody?.();
                          if (body) body.setAttribute('contenteditable', 'false');
                        } catch (e) { }
                        autosaveReadyRef.current = false;
                        return;
                      }
                      autosaveReadyRef.current = false;
                      setTimeout(() => {
                        autosaveReadyRef.current = true;
                      }, 0);
                    }}
                    onChange={(value, editor) => {
                      if (isConsultantUser || !autosaveReadyRef.current) {
                        return;
                      }
                      let textValue = editor.getContent({ format: 'text' });
                      const newWordCount = editor.plugins.wordcount.getCount();
                      debouncedSetActualTextWordCount(newWordCount);


                      // Placeholder : le vrai contentScore est résolu au flush
                      // (évite d'envoyer un score vidé planifié avant le bon analyze).
                      scheduleSave(
                        textValue,
                        value,
                        buildAutoSaveRedactionData(),
                        initialHtmlContent,
                        initialTextContent,
                        'SMART',
                        url || redactionData?.url || '',
                        titleTag || redactionData?.titleTag || '',
                        metaDescriptionTag || redactionData?.metaDescriptionTag || '',
                        null
                      );


                      setActualHtmlContent(value);
                      setActualTextContent(value);
                      setAnalyseText(textValue);

                      checkIfAllLinesGreen(value, websites);
                    }}
                    t={t}
                    orderType="SMART"
                  />
                </div>
              </div>
            )}

            {!translationUi?.isTranslationMode && (
              <>
                <SavingIndicator isSaving={isSaving} />

                <div className="mt-2 mb-2 text-end" style={{ fontSize: '15px', color: '#666', fontWeight: '500' }}>
                  {t('Nombre total de mots ajoutés')} :{' '}
                  <strong style={{ color: '#000' }}>
                    {Math.max(0, (actualTextWordCount || 0) - computedInitialWordCount)}
                  </strong>
                </div>
              </>
            )}

            {!translationUi?.isTranslationMode && (
              <div className="d-flex justify-content-between flex-wrap mt-3">
                <div>{renderTextActionsControls()}</div>
              </div>
            )}
          </div>
        </div>
      </Form>

      {showSidebar && !isTranslationText && (
        <TextDisplaySidebar
          ref={sidebarRef}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          width={sidebarWidth}
          setWidth={setSidebarWidth}
          combinedKeywords={combinedKeywords}
          autoKeywordsGenerationLoading={autoKeywordsGenerationLoading}
          keywords={keywords}
          textType={uiTextType}
          orderContentType={uiTextType}
          websites={websites}
          actualHtmlContent={actualHtmlContent}
          actualTextContent={actualTextContent}
          initialTextContent={initialTextContent}
          isDelivered={redactionData?.delivered}
          userRole={isConsultantUser ? ROLES.CONSULTANT : user.role}
          orderWebsite={
            (order?.website?.urlSite || redactionData?.orderWebsite || '')
          }
          country={websiteCountry}
          t={t}
          keywordStuffingLoading={keywordStuffingLoading}
          keyword={keyword}
          url={url}
          list={list}
          language={projectLanguage}
          maxWords={redactionData?.maxWords || order?.maxWords || 1000}
          wordCount={actualTextWordCount}
          initialContentScore={
            currentContentScore ||
            redactionData?.contentScore ||
            redactionData?.initialScoreData ||
            null
          }
          textId={redactionData?.id || null}
          preCalculatedScoreData={
            currentContentScore ||
            redactionData?.contentScore ||
            redactionData?.initialScoreData ||
            null
          }
          onScoreDataChange={handleScoreDataChange}
          handleInputChange={handleInputChange}
          handleKeywordChange={handleKeywordChange}
          handleGapUpdate={handleGapUpdate}
          checkIfAllLinesGreen={checkIfAllLinesGreen}
          isKeywordBoxReadOnly={isConsultantUser}
          mode="adminClient"
          onGenerateKeywords={() => {
            keywordsAutoGeneration(
              currentRedaction?.orderId || '',
              keyword,
              '',
              url,
              setKeywords,
              setCombinedKeywords,
              setCombinedKeywordsList,
              setWebsites,
              setErrorSuccessMsg,
              setIsSuccess,
              setErrorSuccess,
              t,
              setAutokeywordGenerationLoading
            );
          }}
        />
      )}

      <ComparisonModal
        open={showComparison}
        initialText={initialTextContent}
        text={he.decode(actualTextContent)}
        handleClose={() => setShowComparison(false)}
      />

      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => setConfirmation(false)}
        executeAction={handleSave}
      />

      <KeywordStuffingModal
        open={showKeywordStuffingModal}
        title={keywordStuffingModalTitle}
        message={keywordStuffingModalMsg}
        stuffedKeywords={keywordStuffingModalData}
        showButtonClose={keywordStuffingModalCloseBtn}
        handleClose={() => setShowKeywordStuffingModal(false)}
        executeAction={() => putToDraft('keywordStuffingDraft')}
      />

      <QuitProjectCreationModal
        isOpen={showQuitModal}
        onClose={() => setShowQuitModal(false)}
        title={t('Quitter la rédaction de contenu ?')}
        message={t(
          "Le texte n'a pas été enregistré. Êtes-vous sûr de vouloir quitter la rédaction ?"
        )}
        showSuggestion={false}
        primaryButtonLabel={
          isQuitSaving ? t('Enregistrement...') : t('Enregistrer en brouillon')
        }
        secondaryButtonLabel={t('Quitter sans enregistrer')}
        onSaveDraft={handleSaveDraftAndQuit}
        onQuitWithoutSaving={handleQuitWithoutSaving}
      />
      <QuitProjectCreationModal
        isOpen={showSaveBeforeDownloadModal}
        onClose={() => setShowSaveBeforeDownloadModal(false)}
        title={t('Téléchargement')}
        message={t('Veuillez enregistrer le texte avant de pouvoir le télécharger.')}
        showSuggestion={false}
        showSecondaryButton={false}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => setShowSaveBeforeDownloadModal(false)}
      />

      <QuitProjectCreationModal
        isOpen={isRevisionErrorModalOpen}
        onClose={() => setIsRevisionErrorModalOpen(false)}
        variant="enrichment"
        title={t('Mise en révision impossible')}
        message={t(
          'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
        )}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
        showSecondaryButton={false}
        showSuggestion={false}
      />

      <WordPressDeployModal
        isOpen={showWordPressDeployModal}
        onClose={() => setShowWordPressDeployModal(false)}
        textId={redactionData?.id}
        websiteId={redactionData?.websiteId || order?.websiteId || order?.website?.id}
        initialKeyword={keyword || redactionData?.keyword || ''}
        initialContent={wordPressDeployContent}
        onDeploySuccess={handleWordPressDeploySuccess}
      />

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={async () => {
          setErrorSuccess(false);

          if (shouldReloadAfterSave && savedTextId) {
            await reloadRedactionData(savedTextId);
            setShouldReloadAfterSave(false);
            setSavedTextId(null);
          }

          if (
            errorSuccessMsg ===
            KeywordStuffingModalPrompts.demandeRevisionKeywordStuffing
          ) {
            navigateBack();
          }
        }}
        success={isSuccess}
        isTextDelivered={isTextDelivered}
        resetFields={resetFields}
        redactionId={redactionData?.id}
        showCreditButton={false}
        executeAction={() => { }}
        toggle={() => setErrorSuccess(!errorSuccess)}
        user={user}
        textType={uiTextType}
        imageUrl={errorSuccessImg}
      />
    </div>
  );
}

function AdminClientTextDisplay() {
  const location = useLocation();
  const isConsultantRoute = location.pathname.startsWith('/Consultant');
  usePageTracking(
    isConsultantRoute ? '/Consultant/TextDetails' : ADMIN_CLIENT_TEXT_DETAILS_PAGE
  );

  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <AdminClientTextDisplayContent />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}

export default AdminClientTextDisplay;
